import React, { useState, useEffect, useCallback, useMemo, useOptimistic, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { apiService } from '@/services/api';
import { EventOut, EventOccurrence } from '@/types/api';
import { CalendarCell, CalendarOccurrence } from '@/components/organisms/EventCalendar/types';
import Card from '@/components/atoms/Card';
import Button from '@/components/atoms/Button';
import Icon from '@/components/atoms/Icon/Icon';
import Loader from '@/components/atoms/Loader';
import { useAuth } from '@/contexts/AuthContext';

import './PublicEvents.scss';
import RegistrationEncouragement from '@/components/molecules/RegistrationEncouragement';
import { FormField } from '@/components/molecules/FormField';
import Spacer from '@/components/atoms/Spacer';
import Login from './Login';
import Register from './Register';

const LOADER_FADE_DURATION = 400;

const PublicEvents: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  
  const { isLoading } = useAuth();
  const [events, setEvents] = useState<EventOut[]>([]);
  const [displayEvents, setDisplayEvents] = useState<EventOut[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Intro animation state
  const [gifCompleted, setGifCompleted] = useState(false);
  const [showIntroLoader, setShowIntroLoader] = useState(false);
  const [isFadingOut, setIsFadingOut] = useState(false);
  const [eventVenues, setEventVenues] = useState<{ [eventId: number]: any[] }>({});
  const [filter, setFilter] = useState<'all' | 'upcoming' | 'past'>('all');
  const [searchTerm, setSearchTerm] = useState('');
  // Helper date for reset functionality
  const today = new Date();
  
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [showEventCalendar, setShowEventCalendar] = useState(false);
  const [selectedEventForCalendar, setSelectedEventForCalendar] = useState<EventOut | null>(null);
  const [calendarMonth, setCalendarMonth] = useState(() => new Date());
  const [calendarOccurrences, setCalendarOccurrences] = useState<CalendarOccurrence[]>([]);
  const [showDateFilters, setShowDateFilters] = useState(false);
  const [sortBy, setSortBy] = useState<'date' | 'name' | 'location'>('date');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [expandedEvents, setExpandedEvents] = useState<Set<string>>(new Set());
  const [showRegistrationEncouragement, setShowRegistrationEncouragement] = useState(true);
  const [authModalMode, setAuthModalMode] = useState<'login' | 'register' | null>(null);
  const { isAuthenticated } = useAuth();
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [occurrencesByEventId, setOccurrencesByEventId] = useState<Record<number, EventOccurrence[]>>({});
  const [selectedOccurrenceByEventId, setSelectedOccurrenceByEventId] = useState<Record<number, string>>({});
  const [vipInstructionsEventId, setVipInstructionsEventId] = useState<number | null>(null);

  const [optimisticEvents, _addOptimisticEvent] = useOptimistic(
    displayEvents,
    (state, newEvent: EventOut) => [...state, newEvent]
  );

  const toDateKey = useCallback((value: Date) => {
    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, '0');
    const day = String(value.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }, []);

  const parseLocalDateTime = useCallback((value: string) => {
    const normalized = value.replace(/Z$/, '').replace(/([+-]\d{2}:\d{2})$/, '');
    return new Date(normalized);
  }, []);

  const formatTime = useCallback((value: string) => {
    const parsed = parseLocalDateTime(value);
    if (Number.isNaN(parsed.getTime())) return '';
    return new Intl.DateTimeFormat(i18n.language, {
      hour: '2-digit',
      minute: '2-digit'
    }).format(parsed);
  }, [i18n.language, parseLocalDateTime]);

  const getRecurrenceWindowEnd = useCallback((event: EventOut, startValue: string) => {
    if (!event.recurrence_time_windows) return null;
    const start = parseLocalDateTime(startValue);
    if (Number.isNaN(start.getTime())) return null;
    const dayKey = String((start.getDay() + 6) % 7);
    const window = event.recurrence_time_windows[dayKey];
    if (!window || !window.start_time) return null;

    let end: Date | null = null;
    if (window.end_time) {
      const [endHour, endMinute] = window.end_time.split(':').map(Number);
      end = new Date(start);
      end.setHours(endHour || 0, endMinute || 0, 0, 0);
      if (end <= start) {
        end.setDate(end.getDate() + 1);
      }
    } else if (window.duration_hours && window.duration_hours > 0) {
      end = new Date(start);
      end.setHours(end.getHours() + window.duration_hours);
    }
    return end ? end.toISOString() : null;
  }, [parseLocalDateTime]);

  const weekdayLabels = [
    t('events.calendar.weekdays.mon'),
    t('events.calendar.weekdays.tue'),
    t('events.calendar.weekdays.wed'),
    t('events.calendar.weekdays.thu'),
    t('events.calendar.weekdays.fri'),
    t('events.calendar.weekdays.sat'),
    t('events.calendar.weekdays.sun')
  ];

  const getOccurrenceRange = useCallback(() => {
    const start = dateFrom
      ? new Date(`${dateFrom}T00:00:00`)
      : (() => {
          const now = new Date();
          if (filter === 'past' || filter === 'all') {
            const pastStart = new Date(now);
            pastStart.setFullYear(pastStart.getFullYear() - 1);
            return pastStart;
          }
          return now;
        })();

    const end = dateTo
      ? new Date(`${dateTo}T23:59:59`)
      : (() => {
          const now = new Date();
          if (filter === 'past') {
            return now;
          }
          const futureEnd = new Date();
          futureEnd.setFullYear(futureEnd.getFullYear() + 1);
          return futureEnd;
        })();

    return {
      start_date: start.toISOString(),
      end_date: end.toISOString()
    };
  }, [dateFrom, dateTo, filter]);

  const buildEventsWithOccurrences = useCallback(async (eventsData: EventOut[]) => {
    if (!Array.isArray(eventsData) || eventsData.length === 0) {
      setDisplayEvents([]);
      setOccurrencesByEventId({});
      return;
    }

    const { start_date, end_date } = getOccurrenceRange();
    const recurringEvents = eventsData.filter(event => event.is_recurring);
    const occurrencesByEventId = new Map<number, EventOccurrence[]>();

    await Promise.all(
      recurringEvents.map(async event => {
        try {
          const occurrences = await apiService.getEventOccurrences(event.id, { start_date, end_date });
          occurrencesByEventId.set(event.id, Array.isArray(occurrences) ? occurrences : []);
        } catch (err) {
          console.warn(`Failed to fetch occurrences for event ${event.id}:`, err);
          occurrencesByEventId.set(event.id, []);
        }
      })
    );

    const occurrencesRecord: Record<number, EventOccurrence[]> = {};
    occurrencesByEventId.forEach((occurrences, eventId) => {
      occurrencesRecord[eventId] = occurrences;
    });
    setOccurrencesByEventId(occurrencesRecord);
    setDisplayEvents(eventsData);

    setSelectedOccurrenceByEventId(prev => {
      const next = { ...prev };
      const now = new Date();
      eventsData.forEach(event => {
        if (!event.is_recurring) return;
        const occurrences = occurrencesRecord[event.id] || [];
        if (occurrences.length === 0) return;
        if (next[event.id]) return;
        const sorted = occurrences
          .slice()
          .sort((a, b) => new Date(a.start_date).getTime() - new Date(b.start_date).getTime());
        const upcoming = sorted.find(item => new Date(item.start_date) >= now) || sorted[0];
        if (upcoming?.start_date) {
          next[event.id] = upcoming.start_date;
        }
      });
      return next;
    });
  }, [getOccurrenceRange]);

  const getErrorMessage = useCallback((err: any) => {
    return err.message || t('errors.general');
  }, [t]);

  // Check if we should show intro animation (only when coming from root)
  useEffect(() => {
    const comingFromRoot = location.state?.fromRoot === true;
    
    // Set showIntroLoader to true when coming from root (only once)
    if (comingFromRoot) {
      setShowIntroLoader(true);
    }
  }, [location.state]);

  // Handle intro loader fade out
  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    const shouldDisplayIntroLoader = isLoading || !gifCompleted;

    if (shouldDisplayIntroLoader) {
      setIsFadingOut(false);
    } else if (showIntroLoader && gifCompleted && !isLoading) {
      setIsFadingOut(true);
      timeoutId = setTimeout(() => {
        setShowIntroLoader(false);
        setIsFadingOut(false);
      }, LOADER_FADE_DURATION);
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [isLoading, gifCompleted, showIntroLoader]);

  useEffect(() => {
    document.body.style.overflow = authModalMode ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [authModalMode]);

  useEffect(() => {
    if (isAuthenticated && authModalMode) {
      setAuthModalMode(null);
    }
  }, [isAuthenticated, authModalMode]);

  const handleGifComplete = useCallback(() => {
    setGifCompleted(true);
  }, []);

  const handleLoaderClick = useCallback(() => {
    if (!isLoading) {
      setGifCompleted(true);
    }
  }, [isLoading]);

  useEffect(() => {
    if (isAuthenticated) {
      setShowRegistrationEncouragement(false);
    }
    const fetchEvents = async () => {
      try {
        setLoading(true);
        
        // Use anonymous events endpoint instead of authenticated one
        const eventsData = await apiService.getAnonymousEvents();
        
        
        // Validate events data
        if (!Array.isArray(eventsData)) {
          
          setEvents([]);
          setDisplayEvents([]);
          setEventVenues({});
          return;
        }
        
        setEvents(eventsData);
        await buildEventsWithOccurrences(eventsData);
        
       
        
        // Fetch venues for each event (only if events have valid IDs)
        const venuesData: { [eventId: number]: any[] } = {};
        for (const event of eventsData) {
          // Validate event has an id before fetching venues
          if (!event || typeof event.id !== 'number' || isNaN(event.id)) {
            console.warn('Skipping venue fetch for event without valid id:', event);
            continue;
          }
          
          try {
            const venues = await apiService.getAnonymousEventVenues(event.id);
            venuesData[event.id] = Array.isArray(venues) ? venues : [];
          } catch (err: any) {
            // Log error details for debugging
            console.warn(`Failed to fetch venues for event ${event.id}:`, {
              message: err.message,
              status: err.response?.status,
              statusText: err.response?.statusText,
              data: err.response?.data
            });
            // Set empty array for this event so UI doesn't break
            venuesData[event.id] = [];
          }
        }
        setEventVenues(venuesData);
      } catch (err: any) {
        console.error('Error fetching events:', err);
        console.error('Error details:', {
          message: err.message,
          status: err.response?.status,
          statusText: err.response?.statusText,
          data: err.response?.data
        });
        
        // If it's a network error (backend not running), show sample data
        if (err.message === 'Network Error' || err.code === 'ERR_NETWORK') {
          const sampleEvents: EventOut[] = [
            {
              id: 1,
              name: 'Sample Event 1',
              description: 'This is a sample event for demonstration purposes',
              logo: null,
              start_date: '2024-12-25T20:00:00',
              end_date: '2024-12-25T23:00:00',
              active: true,
              address: '123 Sample Street',
              city: 'Sample City',
              state: 'Sample State',
              country: 'Sample Country'
            },
            {
              id: 2,
              name: 'Sample Event 2',
              description: 'Another sample event to show the interface',
              logo: null,
              start_date: '2024-12-26T19:00:00',
              end_date: '2024-12-26T22:00:00',
              active: true,
              address: '456 Demo Avenue',
              city: 'Demo City',
              state: 'Demo State',
              country: 'Demo Country'
            }
          ];
          setEvents(sampleEvents);
          setDisplayEvents(sampleEvents);
        } else {
          setError(getErrorMessage(err));
        }
      } finally {
        setLoading(false);
      }
    };

    fetchEvents();
  }, [getErrorMessage, isAuthenticated, buildEventsWithOccurrences]);

  const handleFilterChange = useCallback((newFilter: 'all' | 'upcoming' | 'past') => {
    setFilter(newFilter);
  }, []);

  const handleDateFromChange = useCallback((value: string) => {
    setDateFrom(value);
    
    // If dateTo is set and is before or equal to the new dateFrom, clear it
    if (dateTo && value && new Date(dateTo) <= new Date(value)) {
      setDateTo('');
    }
  }, [dateTo]);

  const openEventCalendar = useCallback((event: EventOut) => {
    const selectedOccurrence = selectedOccurrenceByEventId[event.id];
    const baseDate = selectedOccurrence ? parseLocalDateTime(selectedOccurrence) : new Date();
    baseDate.setDate(1);
    baseDate.setHours(0, 0, 0, 0);
    setSelectedEventForCalendar(event);
    setCalendarMonth(baseDate);
    setShowEventCalendar(true);
  }, [parseLocalDateTime, selectedOccurrenceByEventId]);

  const closeEventCalendar = useCallback(() => {
    setShowEventCalendar(false);
    setSelectedEventForCalendar(null);
  }, []);

  const formatDateTime = useCallback((value?: string | null) => {
    if (!value) return t('events.eventCard.tbd');
    const parsed = parseLocalDateTime(value);
    if (Number.isNaN(parsed.getTime())) return t('events.eventCard.tbd');
    return new Intl.DateTimeFormat(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short'
    }).format(parsed);
  }, [parseLocalDateTime, t]);

  const calendarMonthStart = useMemo(() => {
    return new Date(calendarMonth.getFullYear(), calendarMonth.getMonth(), 1);
  }, [calendarMonth]);

  const calendarMonthEnd = useMemo(() => {
    const end = new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() + 1, 0);
    end.setHours(23, 59, 59, 999);
    return end;
  }, [calendarMonth]);

  useEffect(() => {
    const fetchCalendarOccurrences = async () => {
      if (!selectedEventForCalendar) {
        setCalendarOccurrences([]);
        return;
      }

      if (selectedEventForCalendar.is_recurring) {
        try {
          const data = await apiService.getEventOccurrences(selectedEventForCalendar.id, {
            start_date: calendarMonthStart.toISOString(),
            end_date: calendarMonthEnd.toISOString()
          });
          const normalized = Array.isArray(data) ? data : [];
          setCalendarOccurrences(
            normalized.map((occurrence: EventOccurrence) => ({
              event: selectedEventForCalendar,
              start_date: occurrence.start_date,
              end_date: occurrence.end_date
                ?? getRecurrenceWindowEnd(selectedEventForCalendar, occurrence.start_date)
                ?? selectedEventForCalendar.end_date
            }))
          );
        } catch (err) {
          console.warn(`Failed to fetch occurrences for event ${selectedEventForCalendar.id}:`, err);
          setCalendarOccurrences([]);
        }
        return;
      }

      if (selectedEventForCalendar.start_date) {
        const startDate = new Date(selectedEventForCalendar.start_date);
        if (startDate >= calendarMonthStart && startDate <= calendarMonthEnd) {
          setCalendarOccurrences([
            {
              event: selectedEventForCalendar,
              start_date: selectedEventForCalendar.start_date,
              end_date: selectedEventForCalendar.end_date
            }
          ]);
        } else {
          setCalendarOccurrences([]);
        }
      } else {
        setCalendarOccurrences([]);
      }
    };

    if (showEventCalendar) {
      fetchCalendarOccurrences();
    }
  }, [calendarMonthEnd, calendarMonthStart, getRecurrenceWindowEnd, selectedEventForCalendar, showEventCalendar]);

  const calendarOccurrencesByDate = useMemo(() => {
    const map = new Map<string, CalendarOccurrence[]>();
    calendarOccurrences.forEach(item => {
      const parsed = parseLocalDateTime(item.start_date);
      const key = toDateKey(parsed);
      const existing = map.get(key) || [];
      existing.push(item);
      map.set(key, existing);
    });
    return map;
  }, [calendarOccurrences, parseLocalDateTime, toDateKey]);

  const calendarStartOffset = useMemo(() => (calendarMonthStart.getDay() + 6) % 7, [calendarMonthStart]);

  const calendarGridStartDate = useMemo(() => {
    const start = new Date(calendarMonthStart);
    start.setDate(calendarMonthStart.getDate() - calendarStartOffset);
    return start;
  }, [calendarMonthStart, calendarStartOffset]);

  const calendarCells: CalendarCell[] = useMemo(() => {
    return Array.from({ length: 42 }, (_, index) => {
      const date = new Date(calendarGridStartDate);
      date.setDate(calendarGridStartDate.getDate() + index);
      const key = toDateKey(date);
      return {
        date,
        key,
        isCurrentMonth: date.getMonth() === calendarMonthStart.getMonth(),
        occurrences: calendarOccurrencesByDate.get(key) || []
      };
    });
  }, [calendarGridStartDate, calendarMonthStart, calendarOccurrencesByDate]);

  const calendarMonthLabel = useMemo(() => {
    return new Intl.DateTimeFormat(undefined, { month: 'long', year: 'numeric' }).format(calendarMonthStart);
  }, [calendarMonthStart]);

  const toggleEventDescription = useCallback((eventId: string) => {
    setExpandedEvents(prev => {
      const newSet = new Set(prev);
      if (newSet.has(eventId)) {
        newSet.delete(eventId);
      } else {
        newSet.add(eventId);
      }
      return newSet;
    });
  }, []);

  const getEventDisplayDate = useCallback((event: EventOut) => {
    if (!event.is_recurring) {
      return event.start_date ? new Date(event.start_date) : null;
    }
    const selected = selectedOccurrenceByEventId[event.id];
    if (selected) {
      const selectedDate = new Date(selected);
      return Number.isNaN(selectedDate.getTime()) ? null : selectedDate;
    }
    const occurrences = occurrencesByEventId[event.id] || [];
    if (occurrences.length === 0) {
      return event.start_date ? new Date(event.start_date) : null;
    }
    const sorted = occurrences
      .slice()
      .sort((a, b) => new Date(a.start_date).getTime() - new Date(b.start_date).getTime());
    const now = new Date();
    const upcoming = sorted.find(item => new Date(item.start_date) >= now);
    return new Date((upcoming || sorted[0]).start_date);
  }, [occurrencesByEventId, selectedOccurrenceByEventId]);

  const filteredAndSortedEvents = (Array.isArray(optimisticEvents) ? optimisticEvents : [])
    .filter(event => {
      // Search filter
      const matchesSearch = searchTerm === '' || 
        event.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        event.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        event.city?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        event.state?.toLowerCase().includes(searchTerm.toLowerCase());

      if (!matchesSearch) return false;

      // Date range filter - only apply if user has entered dates
      const eventDate = getEventDisplayDate(event);
      if (!eventDate || Number.isNaN(eventDate.getTime())) return true; // Include events without dates
      const now = new Date();
      
      // Apply date range filter only if user has entered dates
      if (dateFrom) {
        const fromDate = new Date(dateFrom);
        if (eventDate < fromDate) return false;
      }
      
      if (dateTo) {
        const toDate = new Date(dateTo);
        if (eventDate > toDate) return false;
      }
      
      // Apply category filter (upcoming/past/all)
      switch (filter) {
        case 'upcoming':
          const isUpcoming = eventDate > now;
          // Log for debugging in development
          if (import.meta.env.DEV && !isUpcoming) {
            
          }
          return isUpcoming;
        case 'past':
          return eventDate < now;
        default:
          return true;
      }
    })
    .sort((a, b) => {
      let comparison = 0;
      
      switch (sortBy) {
        case 'date':
      const dateA = getEventDisplayDate(a)?.getTime() ?? 0;
      const dateB = getEventDisplayDate(b)?.getTime() ?? 0;
          comparison = dateA - dateB;
          break;
        case 'name':
          comparison = a.name.toLowerCase().localeCompare(b.name.toLowerCase());
          break;
        case 'location':
          const locationA = `${a.city || ''} ${a.state || ''}`.toLowerCase();
          const locationB = `${b.city || ''} ${b.state || ''}`.toLowerCase();
          comparison = locationA.localeCompare(locationB);
          break;
      }
      
      return sortOrder === 'desc' ? -comparison : comparison;
    });

  // Show intro loader if it should be displayed
  if (showIntroLoader) {
    return (
      <Loader
        fullScreen
        size="lg"
        onAnimationComplete={handleGifComplete}
        onClick={isLoading ? undefined : handleLoaderClick}
        isFadingOut={isFadingOut}
        showVideo={true}
      />
    );
  }

  if (loading) {
    return <Loader fullScreen size="lg" />;
  }

  if (error) {
    return (
      <div className="container">
        <div className="error">
          <p>{error}</p>
          <Button 
            variant="primary" 
            onClick={() => window.location.reload()}
          >
            {t('common.retry')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      <div className="events-page">
        <div className="events-page__header">
          <div className="events-page__header-content">
            <div className="events-page__header-text">
              <h1 className="events-page__title">{t('events.subtitle')}</h1>
            </div>
          </div>
        </div>
        {/* Registration Encouragement */}
        {showRegistrationEncouragement && (
            <RegistrationEncouragement 
              variant="compact" 
              onSkip={() => setShowRegistrationEncouragement(false)}
              onSignIn={() => setAuthModalMode('login')}
              onSignUp={() => setAuthModalMode('register')}
            />
          )}
        <div className="events-page__search">
          <div className="events-page__search-container">
            <input
              type="text"
              placeholder={t('events.search.placeholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="events-page__search-input"
              ref={searchInputRef}
            />
            {searchTerm && (
              <a
                className="events-page__search-clear"
                onClick={() => {
                  setSearchTerm('');
                  searchInputRef.current?.focus();
                }}
                aria-label={t('events.search.clear')}
              >
                &times;
              </a>
            )}
            <Icon className="events-page__search-icon" name="lupa" size={20} alt={t('events.search.placeholder')} />
          </div>
          <Button
            variant={showDateFilters ? "secondary" : "ghost"}
            size="sm"
            onClick={() => setShowDateFilters(!showDateFilters)}
            className="events-page__filters-toggle"
            title={t('events.filters.title')}
          >
            <Icon name="acciones" size={24} alt={t('events.filters.title')} />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/events/calendar')}
            className="events-page__calendar-toggle"
            title={t('events.calendar.viewCalendar')}
          >
            <Icon name="calendario" size={24} alt={t('events.calendar.viewCalendar')} />
          </Button>
          
        </div>

        {showDateFilters && (
          <div className="events-page__more-filters">
            <div className="events-page__more-filters-header">
              <h4 className="events-page__more-filters-title">
                {t('events.moreFilters.title')}
              </h4>
              <Button
                variant="ghost"
                size="xs"
                className="events-page__more-filters-close"
                onClick={() => setShowDateFilters(!showDateFilters)}
                aria-label={t('common.close')}
              >
                &times;
              </Button>
            </div>
            <Spacer size="1px" className="events-page__more-filters-divider" />
            <div className="events-page__date-filters">
              <div className="events-page__date-inputs">
                  <div className="events-page__sort-group">
                    <FormField 
                      size="sm"
                      type="select"
                      name="sortBy"
                      label={t('events.sort.by')}
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value as 'date' | 'name' | 'location')}
                      className="events-page__sort"
                    >
                      <option value="date">{t('events.sort.date')}</option>
                      <option value="name">{t('events.sort.name')}</option>
                      <option value="location">{t('events.sort.location')}</option>
                    </FormField>
                    <FormField 
                      size="sm"
                      type="select"
                      name="sortOrder"
                      label={t('events.sort.order')}
                      value={sortOrder}
                      onChange={(e) => setSortOrder(e.target.value as 'asc' | 'desc')}
                      className="events-page__sort-select"
                    >
                      <option value="asc">Asc</option>
                      <option value="desc">Desc</option>
                    </FormField>
                </div>
                
                <div className="events-page__date-range-container">
                  <div className="events-page__date-range-label">
                    {t('events.dateFilters.dateRange')}
                  </div>
                  <div className="events-page__date-input-group">
                    <input
                      id="dateFrom"
                      type="date"
                      value={dateFrom}
                      onChange={(e) => handleDateFromChange(e.target.value)}
                      className="events-page__date-input"
                    />
                    <input
                      id="dateTo"
                      type="date"
                      value={dateTo}
                      onChange={(e) => setDateTo(e.target.value)}
                      min={dateFrom || undefined}
                      className="events-page__date-input"
                    />
                  </div>
                </div>
              </div>
            </div>
            <Spacer size="1px" className="events-page__last-separator" />
            <Button
              variant="secondary"
              size="sm"
              fullWidth
              onClick={() => {
                setDateFrom('');
                setDateTo('');
                setSortBy('date');
                setSortOrder('asc');
              }}
              className="events-page__clear-dates"
            >
              {t('events.dateFilters.clear')}
            </Button>
            {/* 
            <div className="events-page__filters">
              <Button
                variant={filter === 'all' ? 'primary' : 'outline'}
                onClick={() => handleFilterChange('all')}
                size="sm"
              >
                {t('events.filters.all')}
              </Button>
              <Button
                variant={filter === 'upcoming' ? 'primary' : 'outline'}
                onClick={() => handleFilterChange('upcoming')}
                size="sm"
              >
                {t('events.filters.upcoming')}
              </Button>
              <Button
                variant={filter === 'past' ? 'primary' : 'outline'}
                onClick={() => handleFilterChange('past')}
                size="sm"
              >
                {t('events.filters.past')}
              </Button>
            </div>
            */}
          </div>
        )}

        {showEventCalendar && selectedEventForCalendar && (
          <div className="events-page__event-calendar-modal">
            <Card variant="elevated" padding="md" className="events-page__event-calendar-card">
              <div className="events-page__event-calendar-header">
                <h4 className="events-page__event-calendar-title">
                  {selectedEventForCalendar.name}
                </h4>
                <Button variant="ghost" size="xs" onClick={closeEventCalendar}>
                  ×
                </Button>
              </div>
              <div className="events-page__event-calendar-controls">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setCalendarMonth(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1))}
                >
                  {t('events.calendar.previous')}
                </Button>
                <span className="events-page__event-calendar-label">{calendarMonthLabel}</span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setCalendarMonth(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1))}
                >
                  {t('events.calendar.next')}
                </Button>
              </div>
              <div className="events-page__event-calendar-weekdays">
                {weekdayLabels.map(label => (
                  <span key={`modal-${label}`} className="events-page__event-calendar-weekday">
                    {label}
                  </span>
                ))}
              </div>
              <div className="events-page__event-calendar-grid">
                {calendarCells.map(cell => (
                  <div
                    key={`modal-${cell.key}`}
                    className={`events-page__event-calendar-cell ${cell.isCurrentMonth ? '' : 'is-outside'}`}
                  >
                    <span className="events-page__event-calendar-date">{cell.date.getDate()}</span>
                    <div className="events-page__event-calendar-occurrences">
                      {cell.occurrences.map(occurrence => (
                        <button
                          key={`modal-${occurrence.event.id}-${occurrence.start_date}`}
                          type="button"
                          className="events-page__event-calendar-occurrence"
                          onClick={() => {
                            setSelectedOccurrenceByEventId(prev => ({
                              ...prev,
                              [occurrence.event.id]: occurrence.start_date
                            }));
                            closeEventCalendar();
                          }}
                        >
                          {formatTime(occurrence.start_date)}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              <div className="events-page__event-calendar-footer">
                <Button variant="secondary" size="sm" onClick={closeEventCalendar}>
                  {t('common.close')}
                </Button>
              </div>
            </Card>
          </div>
        )}

        {authModalMode && (
          <div
            className="events-page__auth-modal"
            onClick={() => setAuthModalMode(null)}
          >
            <Card
              variant="elevated"
              padding="md"
              className="events-page__auth-modal-card"
            >
              <div onClick={(e: React.MouseEvent<HTMLDivElement>) => e.stopPropagation()}>
                <div className="events-page__auth-modal-header">
                  <div className="events-page__auth-modal-tabs">
                    <Button
                      variant={authModalMode === 'login' ? 'primary' : 'ghost'}
                      size="sm"
                      onClick={() => setAuthModalMode('login')}
                    >
                      {t('anonymous.signIn')}
                    </Button>
                    <Button
                      variant={authModalMode === 'register' ? 'primary' : 'ghost'}
                      size="sm"
                      onClick={() => setAuthModalMode('register')}
                    >
                      {t('anonymous.signUp')}
                    </Button>
                  </div>
                  <Button
                    variant="ghost"
                    size="xs"
                    onClick={() => setAuthModalMode(null)}
                    aria-label={t('common.close')}
                  >
                    ×
                  </Button>
                </div>
                <div className="events-page__auth-modal-content">
                  {authModalMode === 'login' ? <Login /> : <Register />}
                </div>
              </div>
            </Card>
          </div>
        )}

        {vipInstructionsEventId !== null && (
          <div
            className="events-page__auth-modal"
            onClick={() => setVipInstructionsEventId(null)}
          >
            <Card
              variant="elevated"
              padding="md"
              className="events-page__auth-modal-card"
            >
              <div onClick={(e: React.MouseEvent<HTMLDivElement>) => e.stopPropagation()}>
                <div className="events-page__auth-modal-header">
                  <h4>{t('events.eventCard.vipInstructionsTitle')}</h4>
                  <Button
                    variant="ghost"
                    size="xs"
                    onClick={() => setVipInstructionsEventId(null)}
                    aria-label={t('common.close')}
                  >
                    ×
                  </Button>
                </div>
                <div className="events-page__auth-modal-content">
                  <p>{t('events.eventCard.vipInstructionsBody')}</p>
                </div>
              </div>
            </Card>
          </div>
        )}

        <div className="events-page__content">
          {filteredAndSortedEvents.length === 0 ? (
            <div className="events-page__empty">
              <p>{t('events.empty')}</p>
            </div>
          ) : (
            <div className="events-page__grid">
              {filteredAndSortedEvents.map((event) => {
                const eventKey = `${event.id}`;
                const eventDate = getEventDisplayDate(event);
                const occurrences = occurrencesByEventId[event.id] || [];
                const selectedOccurrence = selectedOccurrenceByEventId[event.id] || '';
                const sortedOccurrences = occurrences
                  .slice()
                  .sort((a, b) => parseLocalDateTime(a.start_date).getTime() - parseLocalDateTime(b.start_date).getTime());
                const selectedOccurrenceValue = selectedOccurrence || sortedOccurrences[0]?.start_date || '';
                const selectedOccurrenceDate = selectedOccurrenceValue
                  ? parseLocalDateTime(selectedOccurrenceValue)
                  : null;
                return (
                <Card key={eventKey} variant="elevated" className="event-card">
                  {event.logo && (
                    <div className="event-card__image">
                      <img src={event.logo} alt={event.name} />
                    </div>
                  )}
                  <div className="event-card__header">
                    <h3 className="event-card__title">{event.name}</h3>
                    <div className="event-card__data-container">
                      <span className="event-card__location">
                        <Icon name="ubicacion" size={16} alt={t('events.sort.location')} />
                        {event.address || `${event.city}, ${event.state}`}
                      </span>
                      {event.local_name && (
                        <span className="event-card__local">
                          <Icon name="casa" size={16} alt={t('locals.title')} />
                          {event.local_name}
                        </span>
                      )}
                      <span className="event-card__date">
                        <Icon name="calendario" size={16} alt={t('events.sort.date')} />
                        {event.is_recurring && selectedOccurrenceDate
                          ? selectedOccurrenceDate.toLocaleString()
                          : eventDate
                            ? eventDate.toLocaleDateString()
                            : t('events.eventCard.tbd')}
                      </span>
                    </div>
                  </div>
                  
                  <div className="event-card__content">
                    <div className={`event-card__description-wrapper ${expandedEvents.has(eventKey) ? 'expanded' : ''}`}>
                      <p className="event-card__description">
                        {expandedEvents.has(eventKey) 
                          ? event.description 
                          : event.description && event.description.length > 120 
                            ? (() => {
                                const truncated = event.description.substring(0, 120);
                                const lastSpaceIndex = truncated.lastIndexOf(' ');
                                return lastSpaceIndex > 0 ? `${truncated.substring(0, lastSpaceIndex)}...` : `${truncated}...`;
                              })()
                            : event.description
                        }
                        {event.description && event.description.length > 120 && (
                          <a
                            href="#"
                            className="event-card__toggle-description"
                            onClick={(e) => {
                              e.preventDefault();
                              toggleEventDescription(eventKey);
                            }}
                            title={
                              expandedEvents.has(eventKey)
                                ? t('events.eventCard.viewLess')
                                : t('events.eventCard.viewMore')
                            }
                          >
                            {expandedEvents.has(eventKey)
                              ? (
                                  <span className="event-card__toggle-description-text">
                                    {t('events.eventCard.viewLess')}{' '}
                                    <Icon name="arriba" size={16} alt={t('events.eventCard.viewLess')} />
                                  </span>
                                ) : (
                                  <span className="event-card__toggle-description-text">
                                    {t('events.eventCard.viewMore')}{' '}
                                    <Icon name="abajo" size={16} alt={t('events.eventCard.viewMore')} />
                                  </span>
                                )
                            }
                          </a>
                        )}
                      </p>
                    </div>
                    {event.is_recurring && occurrences.length > 0 && (
                      <Button
                        variant="secondary"
                        size="sm"
                        className="event-card__calendar-button"
                        onClick={() => openEventCalendar(event)}
                      >
                        {selectedOccurrenceDate
                          ? formatDateTime(selectedOccurrenceValue)
                          : t('events.eventCard.selectDateTime')}
                      </Button>
                    )}
                  </div>
                  <div className="event-card__actions event-card__actions--dual">
                    <Button
                      className="event-card__actions-btn event-card__actions-btn--pista"
                      variant="primary"
                      size="sm"
                      onClick={() => {
                        const occurrenceParam = selectedOccurrenceByEventId[event.id];
                        const suffix = occurrenceParam ? `?occurrence=${encodeURIComponent(occurrenceParam)}` : '';
                        navigate(`/events/${event.id}/venues/0${suffix}`);
                      }}
                    >
                      {t('events.eventCard.pista')}
                    </Button>
                    <Button
                      className="event-card__actions-btn event-card__actions-btn--vip"
                      variant="secondary"
                      size="sm"
                      onClick={() => setVipInstructionsEventId(event.id)}
                    >
                      {t('events.eventCard.vip')}
                    </Button>
                  </div>
                </Card>
              );
              })}
            </div>
          )}
        </div>

        <div className="events-page__footer">
          <div className="events-page__footer-actions">
            
          </div>
        </div>
      </div>

    </div>
  );
};

export default PublicEvents; 